import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';
import { Link } from 'react-router-dom';
import HeroBackground from '../../components/feature/HeroBackground';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const productsSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${SITE_URL}/products`,
    url: `${SITE_URL}/products`,
    name: 'Product Categories – Industrial Belts, Packaging Machinery & Drives | True Passage',
    description: 'Browse our comprehensive range of industrial products: conveyor belts, power transmission, packaging machinery, VFD drives, and motion control systems from verified manufacturers.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Products', item: `${SITE_URL}/products` },
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: 'True Passage',
      url: SITE_URL,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Product Categories – True Passage',
    url: `${SITE_URL}/products`,
    numberOfItems: 7,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Industrial Belts & Power Transmission', url: `${SITE_URL}/products?filter=belts` },
      { '@type': 'ListItem', position: 2, name: 'Packaging Machinery & Spare Parts', url: `${SITE_URL}/products?filter=packaging` },
      { '@type': 'ListItem', position: 3, name: 'Industrial Drives & Motion Systems', url: `${SITE_URL}/products?filter=drives` },
      { '@type': 'ListItem', position: 4, name: 'Electrical Components & Accessories', url: `${SITE_URL}/products?filter=electrical` },
      { '@type': 'ListItem', position: 5, name: 'Food Processing Molds & Dies', url: `${SITE_URL}/products?filter=molds` },
      { '@type': 'ListItem', position: 6, name: 'Food Processing Ingredients & Commodities', url: `${SITE_URL}/products?filter=ingredients` },
      { '@type': 'ListItem', position: 7, name: 'Industrial Fasteners & Hardware', url: `${SITE_URL}/products?filter=fasteners` },
    ],
  },
];

const ProductsPage = () => {
  const [searchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter) {
      setActiveFilter(filter);
    }
  }, [searchParams]);

  const categories = [
    { id: 'all', name: 'All Products', icon: 'ri-grid-line' },
    { id: 'belts', name: 'Industrial Belts & Power Transmission', icon: 'ri-settings-3-line' },
    { id: 'packaging', name: 'Packaging Machinery & Spare Parts', icon: 'ri-box-3-line' },
    { id: 'drives', name: 'Industrial Drives & Motion Systems', icon: 'ri-flashlight-line' },
    { id: 'electrical', name: 'Electrical Components & Accessories', icon: 'ri-plug-line' },
    { id: 'molds', name: 'Food Processing Molds & Dies', icon: 'ri-shape-2-line' },
    { id: 'ingredients', name: 'Food Processing Ingredients & Commodities', icon: 'ri-flask-line' },
    { id: 'fasteners', name: 'Industrial Fasteners & Hardware', icon: 'ri-tools-fill' },
  ];

  const products = [
    // Industrial Belts & Power Transmission
    {
      id: 1,
      category: 'belts',
      name: 'Conveyor Belts',
      description: 'PU belts, PVC belts, rubber conveyor belts, food-grade belts',
      image: 'https://public.readdy.ai/ai/img_res/da78823dc56ae19cac3d54fac85a59ef.jpg',
      specs: ['Food grade options', 'High abrasion resistance', 'Custom belt widths', 'Continuous operation'],
    },
    {
      id: 2,
      category: 'belts',
      name: 'Power Transmission Belts',
      description: 'V belts, timing belts, flat belts, ribbed belts',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20power%20transmission%20belts%20including%20V%20belts%20timing%20belts%20and%20ribbed%20belts%2C%20various%20sizes%20arranged%20neatly%20on%20white%20background%2C%20precision%20industrial%20components&width=600&height=400&seq=belt2&orientation=landscape',
      specs: ['High torque transfer', 'Heat resistant materials', 'Precision timing control', 'Long service life'],
    },
    {
      id: 3,
      category: 'belts',
      name: 'Industrial Conveyor Systems',
      description: 'Conveyor rollers, belt guides, conveyor modules',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20conveyor%20system%20components%20including%20rollers%20belt%20guides%20and%20modular%20parts%2C%20metallic%20finish%20on%20clean%20white%20background%2C%20heavy%20duty%20manufacturing%20equipment&width=600&height=400&seq=belt3&orientation=landscape',
      specs: ['Custom conveyor layouts', 'Heavy load capacity', 'Modular design', 'Industrial durability'],
    },
    {
      id: 4,
      category: 'belts',
      name: 'Specialty Industrial Belts',
      description: 'Nylon sandwich belts, cover belts, reinforced belts',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20specialty%20industrial%20belts%20with%20multi-layer%20construction%20and%20reinforced%20materials%2C%20nylon%20and%20rubber%20composite%20belts%20on%20white%20background%2C%20heavy%20duty%20industrial%20quality&width=600&height=400&seq=belt4&orientation=landscape',
      specs: ['Multi-layer construction', 'High tensile strength', 'Custom coatings', 'Long operational cycles'],
    },
    {
      id: 5,
      category: 'belts',
      name: 'Belt Components & Accessories',
      description: 'Belt fasteners, joining systems, belt clamps',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20belt%20fasteners%20joining%20systems%20and%20clamps%2C%20metal%20connectors%20and%20accessories%20arranged%20on%20white%20background%2C%20precision%20mechanical%20components&width=600&height=400&seq=belt5&orientation=landscape',
      specs: ['Quick installation', 'Maintenance friendly', 'Heavy duty connectors', 'Conveyor compatibility'],
    },
    {
      id: 6,
      category: 'belts',
      name: 'Pulleys & Drive Components',
      description: 'Belt pulleys, drive rollers, shaft components',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20pulleys%20drive%20rollers%20and%20shaft%20components%2C%20precision%20machined%20metal%20parts%20on%20clean%20white%20background%2C%20mechanical%20engineering%20quality&width=600&height=400&seq=belt6&orientation=landscape',
      specs: ['Precision machining', 'High load tolerance', 'Custom diameters', 'Industrial grade materials'],
    },
    // Packaging Machinery & Spare Parts
    {
      id: 7,
      category: 'packaging',
      name: 'Automatic Packing Machines',
      description: 'VFFS machines, pouch packing machines, sachet fillers',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20automatic%20vertical%20form%20fill%20seal%20VFFS%20packaging%20machine%2C%20modern%20industrial%20equipment%20with%20stainless%20steel%20finish%20on%20clean%20white%20background%2C%20food%20industry%20machinery&width=600&height=400&seq=pack1&orientation=landscape',
      specs: ['High speed packaging', 'Multi product compatibility', 'PLC controlled systems', 'Food industry ready'],
    },
    {
      id: 8,
      category: 'packaging',
      name: 'Filling & Sealing Equipment',
      description: 'Liquid fillers, bottle fillers, sealing machines',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20liquid%20filling%20and%20sealing%20equipment%2C%20stainless%20steel%20bottle%20filler%20machine%20on%20white%20background%2C%20hygienic%20food%20processing%20machinery&width=600&height=400&seq=pack2&orientation=landscape',
      specs: ['Precision filling control', 'Leak proof sealing', 'Stainless steel build', 'Hygienic operation'],
    },
    {
      id: 9,
      category: 'packaging',
      name: 'Wrapping & Shrink Systems',
      description: 'Shrink wrapping machines, carton wrapping systems',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20shrink%20wrapping%20machine%20and%20carton%20wrapping%20system%2C%20automated%20packaging%20equipment%20on%20clean%20white%20background%2C%20modern%20manufacturing%20machinery&width=600&height=400&seq=pack3&orientation=landscape',
      specs: ['Tamper protection', 'Automated wrapping', 'Custom pack sizes', 'High throughput'],
    },
    {
      id: 10,
      category: 'packaging',
      name: 'Packaging Conveyors',
      description: 'Conveyor belts, transfer conveyors, packaging conveyors',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20packaging%20conveyor%20system%20with%20belt%20and%20rollers%2C%20modular%20conveyor%20equipment%20on%20white%20background%2C%20automated%20production%20line%20components&width=600&height=400&seq=pack4&orientation=landscape',
      specs: ['Continuous product flow', 'Adjustable speeds', 'Modular expansion', 'Industrial duty motors'],
    },
    {
      id: 11,
      category: 'packaging',
      name: 'Machine Spare Parts',
      description: 'Sealing jaws, heating elements, machine rollers',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20packaging%20machine%20spare%20parts%20including%20sealing%20jaws%20heating%20elements%20and%20precision%20rollers%2C%20OEM%20quality%20components%20on%20white%20background%2C%20industrial%20replacement%20parts&width=600&height=400&seq=pack5&orientation=landscape',
      specs: ['OEM compatible parts', 'Long service life', 'Fast replacement', 'Precision components'],
    },
    {
      id: 12,
      category: 'packaging',
      name: 'Packaging Control Systems',
      description: 'Control panels, PLC modules, sensors',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20packaging%20control%20panel%20with%20PLC%20modules%20and%20electronic%20sensors%2C%20modern%20automation%20equipment%20on%20clean%20white%20background%2C%20smart%20manufacturing%20technology&width=600&height=400&seq=pack6&orientation=landscape',
      specs: ['Automation ready', 'Smart process control', 'Reliable electronics', 'Industry standard interfaces'],
    },
    // Industrial Drives & Motion Systems
    {
      id: 13,
      category: 'drives',
      name: 'Variable Frequency Drives',
      description: 'VFD drives, AC motor drives',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20variable%20frequency%20drive%20VFD%20units%20and%20AC%20motor%20drives%2C%20modern%20industrial%20control%20equipment%20on%20clean%20white%20background%2C%20automation%20technology&width=600&height=400&seq=drive1&orientation=landscape',
      specs: ['Speed control', 'Energy efficient', 'Overload protection', 'Industrial automation ready'],
    },
    {
      id: 14,
      category: 'drives',
      name: 'Servo Drives & Motion Control',
      description: 'Servo motors, servo drives',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20precision%20servo%20motors%20and%20servo%20drive%20systems%2C%20compact%20industrial%20motion%20control%20equipment%20on%20white%20background%2C%20CNC%20automation%20components&width=600&height=400&seq=drive2&orientation=landscape',
      specs: ['High precision control', 'CNC compatible', 'Rapid response', 'Compact design'],
    },
    {
      id: 15,
      category: 'drives',
      name: 'Gear Drives & Gearboxes',
      description: 'Industrial gearboxes, speed reducers',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20gearboxes%20and%20speed%20reducer%20units%2C%20heavy%20duty%20metal%20gear%20drives%20on%20clean%20white%20background%2C%20precision%20mechanical%20transmission%20systems&width=600&height=400&seq=drive3&orientation=landscape',
      specs: ['Heavy load handling', 'High torque transmission', 'Precision machined gears', 'Long operational life'],
    },
    {
      id: 16,
      category: 'drives',
      name: 'Motor Control Systems',
      description: 'Motor starters, soft starters, controllers',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20motor%20starters%20and%20soft%20starter%20control%20units%2C%20electrical%20control%20equipment%20on%20white%20background%2C%20industrial%20automation%20components&width=600&height=400&seq=drive4&orientation=landscape',
      specs: ['Smooth motor startup', 'Equipment protection', 'Reduced power spikes', 'Industrial reliability'],
    },
    {
      id: 17,
      category: 'drives',
      name: 'Mechanical Drive Systems',
      description: 'Shaft drives, coupling systems',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20mechanical%20shaft%20drives%20and%20coupling%20systems%2C%20precision%20engineered%20metal%20components%20on%20clean%20white%20background%2C%20industrial%20power%20transmission%20parts&width=600&height=400&seq=drive5&orientation=landscape',
      specs: ['High mechanical efficiency', 'Reduced vibration', 'Precision alignment', 'Durable materials'],
    },
    {
      id: 18,
      category: 'drives',
      name: 'Industrial Motion Components',
      description: 'Drive shafts, couplings, mechanical linkages',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20drive%20shafts%20couplings%20and%20mechanical%20linkage%20components%2C%20high%20strength%20metal%20alloy%20parts%20on%20white%20background%2C%20precision%20engineering%20quality&width=600&height=400&seq=drive6&orientation=landscape',
      specs: ['Custom configurations', 'High strength alloys', 'Industrial compatibility', 'Maintenance friendly'],
    },
    // Electrical Components & Accessories
    {
      id: 19,
      category: 'electrical',
      name: 'Industrial Motors',
      description: 'AC motors, DC motors, gear motors',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20AC%20motors%20DC%20motors%20and%20gear%20motors%2C%20heavy%20duty%20electric%20motors%20with%20metallic%20finish%20on%20clean%20white%20background%2C%20precision%20manufacturing%20quality&width=600&height=400&seq=elec1&orientation=landscape',
      specs: ['Energy efficient designs', 'Continuous duty operation', 'Heavy load capability', 'Industrial reliability'],
    },
    {
      id: 20,
      category: 'electrical',
      name: 'Power Distribution Equipment',
      description: 'Circuit breakers, contactors, distribution panels',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20circuit%20breakers%20contactors%20and%20electrical%20distribution%20panels%2C%20modern%20electrical%20safety%20equipment%20on%20white%20background%2C%20industrial%20grade%20components&width=600&height=400&seq=elec2&orientation=landscape',
      specs: ['Electrical protection', 'High current handling', 'Safe power distribution', 'Industrial compliance'],
    },
    {
      id: 21,
      category: 'electrical',
      name: 'Automation Components',
      description: 'PLC systems, industrial sensors, control relays',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20PLC%20programmable%20logic%20controllers%20industrial%20sensors%20and%20control%20relays%2C%20modern%20factory%20automation%20equipment%20on%20clean%20white%20background%2C%20smart%20manufacturing%20technology&width=600&height=400&seq=elec3&orientation=landscape',
      specs: ['Factory automation ready', 'Smart monitoring', 'Reliable signal control', 'Process optimization'],
    },
    {
      id: 22,
      category: 'electrical',
      name: 'Electrical Wiring Accessories',
      description: 'Cable glands, terminal blocks, connectors',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20cable%20glands%20terminal%20blocks%20and%20electrical%20connectors%2C%20precision%20wiring%20accessories%20arranged%20on%20white%20background%2C%20industrial%20electrical%20components&width=600&height=400&seq=elec4&orientation=landscape',
      specs: ['Secure cable management', 'Easy installation', 'Industrial durability', 'Multiple size options'],
    },
    {
      id: 23,
      category: 'electrical',
      name: 'Control Panels & Switchgear',
      description: 'Control cabinets, switch panels',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20control%20panels%20and%20electrical%20switchgear%20cabinets%2C%20modern%20control%20cabinet%20with%20switches%20and%20displays%20on%20white%20background%2C%20industrial%20automation%20equipment&width=600&height=400&seq=elec5&orientation=landscape',
      specs: ['Custom panel builds', 'Safe electrical operation', 'Integrated automation', 'Industrial grade components'],
    },
    {
      id: 24,
      category: 'electrical',
      name: 'Industrial Sensors & Instruments',
      description: 'Proximity sensors, temperature controllers',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20proximity%20sensors%20and%20temperature%20control%20instruments%2C%20precision%20measurement%20devices%20on%20clean%20white%20background%2C%20factory%20automation%20sensors&width=600&height=400&seq=elec6&orientation=landscape',
      specs: ['Precision monitoring', 'Real-time feedback', 'High reliability', 'Industrial compatibility'],
    },
    // Food Processing Molds & Dies
    {
      id: 25,
      category: 'molds',
      name: 'Biscuit & Cookie Molds',
      description: 'Biscuit molds, cookie molds, snack molds',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20biscuit%20and%20cookie%20molds%20with%20intricate%20patterns%2C%20food%20grade%20metal%20molds%20arranged%20on%20clean%20white%20background%2C%20commercial%20bakery%20equipment&width=600&height=400&seq=mold1&orientation=landscape',
      specs: ['Food grade materials', 'Custom shapes', 'High production capacity', 'Easy cleaning'],
    },
    {
      id: 26,
      category: 'molds',
      name: 'Extrusion Dies',
      description: 'Snack extrusion dies, noodle dies',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20food%20extrusion%20dies%20for%20snacks%20and%20noodles%2C%20precision%20metal%20dies%20with%20various%20hole%20patterns%20on%20white%20background%2C%20food%20manufacturing%20equipment&width=600&height=400&seq=mold2&orientation=landscape',
      specs: ['Precision shaping', 'Continuous production', 'Durable metal dies', 'Consistent product size'],
    },
    {
      id: 27,
      category: 'molds',
      name: 'Chocolate & Candy Molds',
      description: 'Chocolate molds, confectionery molds',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20chocolate%20and%20candy%20molds%20with%20detailed%20patterns%2C%20food%20safe%20silicone%20and%20metal%20molds%20on%20clean%20white%20background%2C%20confectionery%20manufacturing%20equipment&width=600&height=400&seq=mold3&orientation=landscape',
      specs: ['High detail patterns', 'Food safe materials', 'Reusable designs', 'Custom branding options'],
    },
    {
      id: 28,
      category: 'molds',
      name: 'Bakery Production Molds',
      description: 'Cake molds, bread molds, baking trays',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20commercial%20bakery%20molds%20including%20cake%20pans%20bread%20molds%20and%20baking%20trays%2C%20industrial%20baking%20equipment%20on%20white%20background%2C%20food%20service%20quality&width=600&height=400&seq=mold4&orientation=landscape',
      specs: ['Heat resistant materials', 'Commercial bakery ready', 'Easy release surfaces', 'Long life cycles'],
    },
    {
      id: 29,
      category: 'molds',
      name: 'Wafer & Specialty Snack Molds',
      description: 'Wafer molds, specialty snack molds',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20wafer%20molds%20and%20specialty%20snack%20production%20molds%2C%20precision%20food%20manufacturing%20dies%20on%20clean%20white%20background%2C%20commercial%20food%20processing%20equipment&width=600&height=400&seq=mold5&orientation=landscape',
      specs: ['Consistent shaping', 'Industrial durability', 'High production speeds', 'Custom designs'],
    },
    {
      id: 30,
      category: 'molds',
      name: 'Custom Food Production Dies',
      description: 'Custom industrial food molds and dies',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20custom%20industrial%20food%20production%20molds%20and%20dies%20with%20various%20shapes%2C%20precision%20engineered%20food%20manufacturing%20tools%20on%20white%20background%2C%20OEM%20food%20processing%20equipment&width=600&height=400&seq=mold6&orientation=landscape',
      specs: ['Tailored manufacturing', 'OEM compatibility', 'High precision tooling', 'Food industry compliance'],
    },
    // Food Processing Ingredients & Commodities
    {
      id: 31,
      category: 'ingredients',
      name: 'Flavoring Agents',
      description: 'Natural flavors, synthetic flavors, flavor concentrates',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20food%20flavoring%20agents%20in%20bottles%20and%20containers%2C%20natural%20and%20synthetic%20flavor%20concentrates%20arranged%20on%20clean%20white%20background%2C%20food%20industry%20ingredients&width=600&height=400&seq=ingr1&orientation=landscape',
      specs: ['Food industry compliant', 'Strong flavor profiles', 'Long shelf life', 'Bulk supply'],
    },
    {
      id: 32,
      category: 'ingredients',
      name: 'Food Coloring Solutions',
      description: 'Natural food colors, liquid colors, powder colors',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20food%20coloring%20solutions%20in%20various%20forms%20including%20liquid%20and%20powder%20colors%2C%20vibrant%20food%20dyes%20in%20containers%20on%20white%20background%2C%20food%20processing%20ingredients&width=600&height=400&seq=ingr2&orientation=landscape',
      specs: ['Vibrant color stability', 'Food safe ingredients', 'Custom blends', 'Bulk packaging'],
    },
    {
      id: 33,
      category: 'ingredients',
      name: 'Baking & Processing Additives',
      description: 'Baking improvers, stabilizers, emulsifiers',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20baking%20additives%20including%20improvers%20stabilizers%20and%20emulsifiers%2C%20food%20processing%20ingredients%20in%20bags%20and%20containers%20on%20clean%20white%20background%2C%20commercial%20baking%20supplies&width=600&height=400&seq=ingr3&orientation=landscape',
      specs: ['Improve texture', 'Enhance shelf life', 'Food grade standards', 'Industrial baking support'],
    },
    {
      id: 34,
      category: 'ingredients',
      name: 'Sweeteners & Sugar Alternatives',
      description: 'Glucose syrups, sweeteners, sugar substitutes',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20sweeteners%20and%20sugar%20alternatives%20including%20glucose%20syrup%20and%20sugar%20substitutes%2C%20food%20processing%20sweetening%20agents%20in%20containers%20on%20white%20background%2C%20industrial%20food%20ingredients&width=600&height=400&seq=ingr4&orientation=landscape',
      specs: ['Consistent sweetness', 'Food processing ready', 'High purity', 'Bulk supply options'],
    },
    {
      id: 35,
      category: 'ingredients',
      name: 'Food Preservatives',
      description: 'Citric acid, sorbates, benzoates',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20food%20preservatives%20including%20citric%20acid%20sorbates%20and%20benzoates%2C%20food%20grade%20preservation%20chemicals%20in%20containers%20on%20clean%20white%20background%2C%20food%20safety%20ingredients&width=600&height=400&seq=ingr5&orientation=landscape',
      specs: ['Shelf life extension', 'Quality preservation', 'Industry approved', 'Safe storage stability'],
    },
    {
      id: 36,
      category: 'ingredients',
      name: 'Bulk Food Commodities',
      description: 'Cocoa powder, starches, specialty food powders',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20bulk%20food%20commodities%20including%20cocoa%20powder%20starches%20and%20specialty%20food%20powders%2C%20industrial%20food%20ingredients%20in%20bags%20and%20containers%20on%20white%20background%2C%20commercial%20food%20supply&width=600&height=400&seq=ingr6&orientation=landscape',
      specs: ['Industrial food supply', 'Reliable sourcing', 'Bulk shipments', 'Consistent quality'],
    },
    // Industrial Fasteners & Hardware
    {
      id: 37,
      category: 'fasteners',
      name: 'Bolts & Nuts',
      description: 'Hex bolts, flange bolts, heavy nuts',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20industrial%20hex%20bolts%20flange%20bolts%20and%20heavy%20duty%20nuts%2C%20precision%20metal%20fasteners%20arranged%20on%20clean%20white%20background%2C%20mechanical%20hardware%20components&width=600&height=400&seq=fast1&orientation=landscape',
      specs: ['High tensile strength', 'Industrial grade steel', 'Corrosion resistant', 'Multiple sizes'],
    },
    {
      id: 38,
      category: 'fasteners',
      name: 'Screws & Threaded Fasteners',
      description: 'Machine screws, self tapping screws',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20machine%20screws%20and%20self%20tapping%20screws%2C%20precision%20threaded%20fasteners%20with%20various%20head%20types%20on%20white%20background%2C%20industrial%20hardware%20components&width=600&height=400&seq=fast2&orientation=landscape',
      specs: ['Precision threading', 'Durable finishes', 'Multiple head types', 'Bulk supply'],
    },
    {
      id: 39,
      category: 'fasteners',
      name: 'Washers & Spacers',
      description: 'Flat washers, spring washers, industrial spacers',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20flat%20washers%20spring%20washers%20and%20industrial%20spacers%2C%20metal%20hardware%20components%20arranged%20on%20clean%20white%20background%2C%20precision%20mechanical%20parts&width=600&height=400&seq=fast3&orientation=landscape',
      specs: ['Load distribution', 'Anti vibration support', 'Durable materials', 'Industrial reliability'],
    },
    {
      id: 40,
      category: 'fasteners',
      name: 'Anchors & Expansion Bolts',
      description: 'Anchor bolts, expansion anchors',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20anchor%20bolts%20and%20expansion%20anchors%2C%20heavy%20duty%20concrete%20fastening%20hardware%20on%20white%20background%2C%20structural%20installation%20components&width=600&height=400&seq=fast4&orientation=landscape',
      specs: ['Heavy load fixing', 'Concrete installation', 'Corrosion resistant', 'Structural support'],
    },
    {
      id: 41,
      category: 'fasteners',
      name: 'Rivets & Industrial Pins',
      description: 'Blind rivets, structural rivets, pins',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20blind%20rivets%20structural%20rivets%20and%20industrial%20pins%2C%20permanent%20fastening%20hardware%20on%20clean%20white%20background%2C%20precision%20metal%20joining%20components&width=600&height=400&seq=fast5&orientation=landscape',
      specs: ['Permanent fastening', 'High strength joints', 'Easy installation', 'Multiple materials'],
    },
    {
      id: 42,
      category: 'fasteners',
      name: 'Stainless Steel Fasteners',
      description: 'Stainless bolts, screws, nuts',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20photography%20of%20stainless%20steel%20bolts%20screws%20and%20nuts%2C%20corrosion%20resistant%20metal%20fasteners%20with%20polished%20finish%20on%20white%20background%2C%20marine%20grade%20hardware&width=600&height=400&seq=fast6&orientation=landscape',
      specs: ['Corrosion resistance', 'Marine grade options', 'Long service life', 'High durability'],
    },
  ];

  const filteredProducts =
    activeFilter === 'all'
      ? products
      : products.filter((p) => p.category === activeFilter);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Product Categories – Industrial Belts, Packaging Machinery & Drives | True Passage"
        description="Browse verified industrial products: conveyor belts, power transmission, packaging machinery, VFD drives, servo systems, and motion control from trusted manufacturers. Request a quote today."
        keywords="industrial belts, conveyor belts, power transmission, packaging machinery, VFD drives, servo motors, gearboxes, motion control systems"
        canonical="/products"
        schema={productsSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-navy-primary via-navy-secondary to-navy-primary overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Our Product
              <br />
              <span className="text-gold-accent">Categories</span>
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              Explore our comprehensive range of industrial products sourced from verified manufacturers
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-neutral-light sticky top-20 z-40 border-b border-navy-primary/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all whitespace-nowrap ${
                  activeFilter === category.id
                    ? 'bg-gold-accent text-navy-primary shadow-md'
                    : 'bg-white text-navy-primary border-2 border-navy-primary/20 hover:border-gold-accent'
                }`}
              >
                <i className={`${category.icon}`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20" data-product-shop>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                className="bg-white border-2 border-navy-primary/10 rounded-2xl overflow-hidden hover:border-gold-accent hover:shadow-xl transition-all group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <div className="relative w-full h-64 bg-neutral-light overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-navy-primary mb-2">
                    {product.name}
                  </h3>
                  <p className="text-sm text-navy-primary/70 mb-4 leading-relaxed">
                    {product.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.specs.map((spec) => (
                      <span
                        key={spec}
                        className="px-3 py-1 bg-gold-accent/10 text-navy-primary text-xs rounded-full"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>

                  <Link
                    to="/request-quote"
                    className="inline-flex items-center gap-2 text-gold-accent font-semibold text-sm hover:gap-3 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Request Quote
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            className="bg-gradient-to-br from-navy-primary to-navy-secondary rounded-3xl p-12 lg:p-16 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-white mb-5">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
              We can source custom products tailored to your specific requirements
            </p>
            <Link
              to="/request-quote"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gold-accent text-navy-primary rounded-full font-semibold text-base hover:scale-105 transition-transform shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Request Quote
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;